import { ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../../validate/details/Reports.scss";
import { StopWarningBanner, StopInfo } from "../../validate/details/StopWarningBanner";
import { updateGsStatus } from "../../../store/MissionSlice";
import useSatellite from "../hooks/useSatellite";
import useLoader from "../hooks/useLoader";
import useViewer from "../hooks/useViewer";
import * as Cesium from 'cesium';
import { get_current_contact_details, get_current_onboard_details, isOperateMode } from "../hooks/utils";
import useScenario from "../hooks/useScenario";
import useContacts from "../hooks/useContacts";
import useUrlParams from "../hooks/useUrlParams";
import useSocket from "../hooks/useSocket";
import { WS_CONTACT_EVENTS, WS_GROUNDSTATION_EVENTS, WS_SCHEDULE_EVENTS, WS_VALIDATE_ABORT_EVENT, CUSTOM_EVENT_REFRESH_ONBOARD_SCHEDULE } from "../../../utils/constants";
import MissionService from "../../../service/MissionService";
import { SelectedTaskplanType } from "../types";
import useCustomSchedule from "../hooks/useCustomSchedule";
import { SCENARIO_DEPLOYMENT_TYPES } from "../../simulate/constants";

interface ContactsProviderProps {
    children: ReactNode;
}

const ContactsProvider = ({ children }: ContactsProviderProps) => {
    const navigate = useNavigate();
    const { viewer } = useViewer()
    const { socket } = useSocket()
    const [abortModalData, setAbortModalData] = useState<StopInfo | null>(null);
    const { setLoading } = useLoader()
    const { selected_satellite } = useSatellite()
    const { simulation_time, scenario } = useScenario()
    const { setTaskPlanDetails } = useCustomSchedule()
    const { scenario_id, sat_id, dashboard, mission_id, sat_grp_id, tab_name } = useUrlParams()
    const { contacts, scheduleData, selectedScheduleTask, timelineContactData, selectedContact, setSelectedScheduleTask, setContacts, setSelectedContact, get_all_ground_stations, get_all_onboard_schedules, get_configured_gs, get_onboard_schedule_data, get_gs_contact_details, get_time_based_gs_contact_details } = useContacts()
    const dispatch = useDispatch()
    const operateMode = isOperateMode(dashboard, scenario)
    const isTTCCommandCenter = scenario?.scenarioDeploymentType === 'TRUETWIN_COMMAND_CENTER';
    const lastScenarioOnboardRef = useRef<string | null>(null);
    const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()
    const isValidateSummary = deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE && dashboard === 'summary'

    useEffect(() => {
        if (!sat_grp_id) return;

        const fetchData = async () => {
            try {
                const [taskResponse, taskPlanResponse] = await Promise.all([
                    MissionService.getAllTask(sat_grp_id),
                    MissionService.getAllTaskPlan(sat_grp_id),
                ]);

                // Handle Adhoc Tasks
                let adhocTasks: any[] = [];
                if (taskResponse?.data) {
                    adhocTasks = taskResponse.data.filter(
                        (task: any) => task.taskType === "Adhoc"
                    );
                }

                // Handle Task Plans
                const taskPlans: SelectedTaskplanType[] =
                    taskPlanResponse?.data ?? [];

                setTaskPlanDetails({
                    allTaskPlans: taskPlans,
                    allAdhocTasks: adhocTasks,
                })
            } catch (error) {
                console.error("Error fetching tasks or task plans:", error);
            }
        };

        fetchData();
    }, [sat_grp_id]);



    useEffect(() => {
        get_all_ground_stations()
    }, [])


    useEffect(() => {
        get_configured_gs()
        if (!mission_id) return
        MissionService.get_mission_by_id(mission_id)?.then((res) => {
            const gsList: any[] = res?.data?.['gsList'] ?? []
            gsList.forEach((gs: any) => {
                if (gs?.groundStationId) {
                    dispatch(updateGsStatus({ gsId: gs.groundStationId, status: gs?.isActive ?? true }))
                }
            })
        }).catch((err) => {
            console.error('Failed to fetch mission GS status:', err)
        })
    }, [sat_id, mission_id])

    useEffect(() => {
        if (!scenario_id || (!isTTCCommandCenter && !operateMode && dashboard !== 'operate')) return;
        if (lastScenarioOnboardRef.current === scenario_id) return;
        // get_ongoing_schedule() // need in future for ongoing schedule
        get_all_onboard_schedules()
        lastScenarioOnboardRef.current = scenario_id;
    }, [scenario_id, isTTCCommandCenter, get_all_onboard_schedules, dashboard, operateMode])

    useEffect(() => {
        const handleRefresh = () => {
            if (scenario_id && operateMode) {
                get_all_onboard_schedules();
            }
        };
        window.addEventListener(CUSTOM_EVENT_REFRESH_ONBOARD_SCHEDULE, handleRefresh);
        return () => {
            window.removeEventListener(CUSTOM_EVENT_REFRESH_ONBOARD_SCHEDULE, handleRefresh);
        };
    }, [scenario_id, operateMode, get_all_onboard_schedules]);

    useEffect(() => {
        if (selected_satellite?.value?.['simulationId']) {
            // get_timeline_onboard_schedule_data()
            get_onboard_schedule_data()
        }
        if (isValidateSummary) {
            setLoading('contacts', false)
            return
        }
        get_gs_contact_details()
    }, [selected_satellite, isValidateSummary])

    useEffect(() => {
        if (isValidateSummary) {
            setLoading('contacts', false)
            return
        }
        if (scenario_id && operateMode && simulation_time?.start_time && simulation_time?.end_time && sat_id) {
            get_time_based_gs_contact_details(scenario_id, sat_id, simulation_time?.start_time, simulation_time?.end_time, 'compact')
        }
    }, [isValidateSummary, simulation_time?.start_time, simulation_time?.end_time, operateMode, scenario_id, sat_id])


    useEffect(() => {
        if (!socket) return;

        const handleMessage = (event: MessageEvent) => {
            try {
                const data = JSON.parse(event.data);

                // data.message may be a plain event string OR a JSON-encoded inner payload
                // e.g. { message: '{"groundStationId":"...","message":"GROUNDSTATION_DISABLED"}' }
                let inner: any = null;
                let eventType: string = data.eventType ?? data.event ?? data.type ?? '';
                if (!eventType) {
                    try {
                        inner = JSON.parse(data.message);
                        eventType = inner?.message ?? inner?.eventType ?? inner?.event ?? inner?.type ?? data.message;
                    } catch {
                        eventType = data.message ?? '';
                    }
                }

                // Handle contact events
                if (eventType && (WS_CONTACT_EVENTS as Set<string>).has(eventType)) {
                    if (scenario_id && operateMode && sat_id && simulation_time?.start_time && simulation_time?.end_time) {
                        get_time_based_gs_contact_details(
                            scenario_id,
                            sat_id,
                            simulation_time.start_time,
                            simulation_time.end_time,
                            'compact'
                        );
                    }
                    return;
                }

                // Handle schedule events
                if (eventType && (WS_SCHEDULE_EVENTS as Set<string>).has(eventType)) {
                    if (scenario_id && operateMode) {
                        get_all_onboard_schedules();
                    }
                }

                // ─── Global Test Case Abort Listener ───
                // If a validation run is aborted due to a critical command failure, the backend
                // broadcasts a VALIDATION_TEST_CASE_ABORTED event. We capture this event globally
                // to trigger a high-visibility floating warning card in the top-right corner.
                if (
                    eventType === WS_VALIDATE_ABORT_EVENT ||
                    eventType === 'TEST_CASE_EXECUTION_ABORTED' ||
                    eventType === 'TEST_PLAN_EXECUTION_ABORTED' ||
                    eventType === 'TEST_CASE_EXECUTION_SKIPPED' ||
                    eventType === 'TEST_CASE_EXECUTION_PARTIALLY_COMPLETED'
                ) {
                    const eventData = data?.data || {};
                    const stopInfo = eventData.stopInfo || eventData.skipInfo || inner?.stopInfo || inner;

                    setAbortModalData({
                        eventType: eventType,
                        stopReasonCode: eventType,
                        stopReasonMessage: eventData.abortReason || eventData.skipReason || stopInfo?.stopReasonMessage || 'An execution alert occurred.',
                        stoppedAtOperationId: eventData.failedEntryOperationId || stopInfo?.stoppedAtOperationId,
                        stoppedAtEntryIndex: eventData.failedEntryIndex != null ? eventData.failedEntryIndex : stopInfo?.stoppedAtEntryIndex,
                        appliedFailureHandling: eventData.skipInfo?.failureMode || inner?.appliedFailureHandling || stopInfo?.appliedFailureHandling,
                        testCaseName:
                            eventData.failedTestCaseId ||
                            eventData.testCaseId ||
                            inner?.testCaseName ||
                            stopInfo?.testCaseName ||
                            inner?.testCase?.testCaseName ||
                            stopInfo?.testCase?.testCaseName ||
                            inner?.testCaseId ||
                            stopInfo?.testCaseId ||
                            inner?.testCase?.id ||
                            stopInfo?.testCase?.id,
                        failedTestCaseId: eventData.failedTestCaseId,
                        failedTestCaseName: eventData.failedTestCaseName,
                        failedCommandId: eventData.failedCommandId,
                        failedCommandName: eventData.failedCommandName,
                        validationFailReason: eventData.validationFailReason,
                        testPlanId: eventData.testPlanId,
                        stoppedAt: eventData.abortedAt || eventData.skippedAt || eventData.stoppedAt || stopInfo?.stoppedAt,
                    });
                    return;
                }

                if (eventType && (WS_GROUNDSTATION_EVENTS as Set<string>).has(eventType)) {
                    const gsId: string = inner?.groundStationId;
                    if (gsId && scenario_id && operateMode) {
                        const status = eventType !== 'GROUNDSTATION_DISABLED';
                        dispatch(updateGsStatus({ gsId, status }));
                        // Re-fetch configured GS so the Cesium entity list is up to date
                        get_configured_gs();
                    }
                    return;
                }
            } catch (error) {
                console.error("Error parsing message:", error, event.data);
            }
        };

        socket.onmessage = handleMessage;
    }, [isValidateSummary, simulation_time?.start_time, simulation_time?.end_time, operateMode, scenario_id, sat_id, socket, get_all_onboard_schedules, get_time_based_gs_contact_details])




    useEffect(() => {
        if (isValidateSummary) {
            setLoading('contacts', false)
            return
        }
        if (viewer) {
            const Interval = setInterval(() => {
                const clock = viewer?.clock;
                if (clock) {
                    const cesium_time = clock.currentTime;
                    if (cesium_time) {
                        const current_time = Cesium.JulianDate.toDate(cesium_time).getTime() / 1000
                        const selectedSchedule = get_current_onboard_details(current_time, scheduleData, selectedScheduleTask)
                        const contactDetails = get_current_contact_details(current_time, timelineContactData, selectedContact,)
                        setSelectedScheduleTask(selectedSchedule || selectedScheduleTask)
                        setContacts(contactDetails?.contacts || contacts)
                        setSelectedContact(contactDetails?.selected_contact || selectedContact)
                        setLoading('contacts', false)
                        setLoading('schedule', false)
                    }
                }
            }, 1000);
            return () => clearInterval(Interval);
        }
    }, [isValidateSummary, viewer, scheduleData, selectedScheduleTask, timelineContactData, selectedContact]);


    const handleViewReport = () => {
        setAbortModalData(null);
        const currentPath = window.location.pathname;
        const pathSegments = currentPath.split('/');
        const tabs = ['hud', 'plan', 'monitor', 'files', 'reports'];
        const lastSegment = pathSegments[pathSegments.length - 1]?.toLowerCase();
        if (tabs.includes(lastSegment)) {
            pathSegments[pathSegments.length - 1] = 'reports';
        } else {
            pathSegments.push('reports');
        }
        navigate(pathSegments.join('/'));
    };

    return (
        <>
            {children}
            {abortModalData && (
              <StopWarningBanner
                stopInfo={abortModalData}
                appliedFailureHandling={abortModalData.appliedFailureHandling}
                onDismiss={() => setAbortModalData(null)}
                onViewReport={handleViewReport}
                isGlobal
              />
            )}
        </>
    );

}

export default ContactsProvider;
